import { Box } from '@mui/material';
import { MenuExpandButton } from './MenuExpandButton';
import { Logo } from './Logo';
import { useCallback, useState } from 'react';

interface MenuExpansionControlProps {
  menuOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  isMobile: boolean;
}

export const MenuExpansionControl = ({
  menuOpen,
  openMenu,
  closeMenu,
}: MenuExpansionControlProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const toggleExpanded = useCallback(() => {
    if (menuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [menuOpen, closeMenu, openMenu]);

  const handleMouseEnter = useCallback(() => {
    if (!menuOpen) {
      setIsHovered(true);
    }
  }, [menuOpen]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // When collapsed: logo visible unless hovered, button visible on hover
  // When expanded: both always visible, no hover effects
  const showLogo = menuOpen || !isHovered;
  const showButton = menuOpen || isHovered;

  return (
    <Box
      sx={{
        position: 'relative',
        whiteSpace: 'nowrap',
        alignSelf: 'flex-start',
        maxWidth: '100%',
        width: '100%',
        height: '68px',
        padding: '16px 18px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {/* Logo - absolute so it doesn't affect button position */}
      <Box
        onMouseEnter={handleMouseEnter}
        sx={{
          position: 'absolute',
          left: '18px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          opacity: showLogo ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out',
          pointerEvents: showLogo ? 'auto' : 'none',
        }}
      >
        <Logo />
      </Box>

      {/* Spacer to push button to the end */}
      <Box sx={{ flex: 1 }} />

      {/* Button - always at end in normal flow */}
      <Box
        onMouseLeave={handleMouseLeave}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: showButton ? 1 : 0,
          transition: 'opacity 0.15s ease-in-out',
          pointerEvents: showButton ? 'auto' : 'none',
        }}
      >
        <MenuExpandButton expanded={menuOpen} toggleExpanded={toggleExpanded} />
      </Box>
    </Box>
  );
};
